import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { IcsEvaluationActions } from './ics-evaluation.actions';
import { IcsEvaluationService } from '../../service/ics-evaluation.service';
import { Store } from '@ngrx/store';
import { selectEvaluationCampaignId } from '../campaign-dashboard/campaign-dashboard.reducer';
import { IcsEvaluation } from '../../../../graphql/generated';


@Injectable()
export class IcsEvaluationEffects {

  loadIcsEvaluations$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.loadIcsEvaluation),
        mergeMap(({ icsCampaignId, parameters, fields }) =>
          cd.getIcsEvaluation(icsCampaignId, parameters).pipe(
            map((res) => {
              return IcsEvaluationActions.loadIcsEvaluationSuccess({
                ics: res,
                fields
              });
            }),
            catchError((error) => of(IcsEvaluationActions.loadIcsEvaluationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllIcsEvaluations$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.loadAllIcsEvaluation),
        mergeMap(({ icsCampaignId, parameters, fields }) =>
          cd.getIcsEvaluation(icsCampaignId, parameters).pipe(
            map((res) => {
              return IcsEvaluationActions.loadAllIcsEvaluationSuccess({
                ics: res,
                fields
              });
            }),
            catchError((error) => of(IcsEvaluationActions.loadAllIcsEvaluationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateIcsEvaluation$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.updateIcsEvaluation),
        mergeMap(({ icsEvaluationUpdates, loadingCells }) =>
          cd.updateIcsEvaluation(icsEvaluationUpdates).pipe(
            map((res) => {
              return IcsEvaluationActions.updateIcsEvaluationSuccess({
                ics: res,
                loadingCells
              });
            }),
            catchError((error) => of(IcsEvaluationActions.updateIcsEvaluationFailure({ error, loadingCells })))
          )
        )
      );
    },
    { functional: true }
  );

  updateIcsEvaluationQuantitativeScores$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.updateIcsEvaluationQuantitativeScores),
        mergeMap(({ input, loadingCells }) =>
          cd.updateIcsEvaluationQuantitativeScores(input).pipe(
            map((res) => {
              if (res?.statusCode === 400) {
                return IcsEvaluationActions.updateIcsEvaluationQuantitativeScoresFailure({
                  error: res?.message ?? 'Update rejected: no changes were saved',
                  loadingCells
                });
              }
              return IcsEvaluationActions.updateIcsEvaluationQuantitativeScoresSuccess({
                ics: res?.data?.[0] as IcsEvaluation,
                loadingCells
              });
            }),
            catchError((error) => of(IcsEvaluationActions.updateIcsEvaluationQuantitativeScoresFailure({ error, loadingCells })))
          )
        )
      );
    },
    { functional: true }
  );

  updateIcsEvaluationQuantitativeScoresFailureMessage$ = createEffect(
    (messageService: MessageService = inject(MessageService)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.updateIcsEvaluationQuantitativeScoresFailure),
        map(({ error }) => {
          messageService.add({
            severity: 'error',
            summary: 'Update rejected',
            detail: typeof error === 'string' ? error : 'No changes were saved',
            life: 5000
          });
        })
      );
    },
    { functional: true, dispatch: false }
  );

  updateLocalChiefConfirmationStatus$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService), store: Store = inject(Store)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.updateLocalChiefConfirmationStatus),
        withLatestFrom(store.select(selectEvaluationCampaignId)),
        // mergeMap e non switchMap: gli update sono per riga, con switchMap un update
        // su un'altra riga annullerebbe il precedente lasciando la cella in loading.
        mergeMap(([{ evaluationId, localChiefConfirmationStatus, loadingCells }, campaignId]) =>
          cd.updateLocalChiefConfirmationStatus(evaluationId, localChiefConfirmationStatus).pipe(
            switchMap((res) => {
              return of(IcsEvaluationActions.updateLocalChiefConfirmationStatusSuccess({
                evaluation: res,
                loadingCells
              }));
            }),
            catchError((error) => of(IcsEvaluationActions.updateLocalChiefConfirmationStatusFailure({ error, id: evaluationId, loadingCells })))
          )
        )
      );
    },
    { functional: true }
  );

  downloadIcsEvaluation$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.downloadIcsEvaluation),
        mergeMap(({ query, icsCampaignId, timeZone }) =>
          cd.downloadIcsEvaluation(query, icsCampaignId, timeZone).pipe(
            map((res: any) => {
              const filename = res?.data?.[0]?.filename || 'export.xlsx';
              return IcsEvaluationActions.downloadIcsEvaluationSuccess({
                downloadUrl: '',
                fileName: filename
              });
            }),
            catchError((error) => of(IcsEvaluationActions.downloadIcsEvaluationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateGaltConfirmationStatus$ = createEffect(
    (cd: IcsEvaluationService = inject(IcsEvaluationService), store: Store = inject(Store)) => {
      return this.actions$.pipe(
        ofType(IcsEvaluationActions.updateGaltConfirmationStatus),
        withLatestFrom(store.select(selectEvaluationCampaignId)),
        // mergeMap e non switchMap: vedi updateLocalChiefConfirmationStatus$
        mergeMap(([{ evaluationId, galtConfirmationStatus, loadingCells }, campaignId]) =>
          cd.updateGaltConfirmationStatus(evaluationId, galtConfirmationStatus).pipe(
            switchMap((res) => {
              return of(IcsEvaluationActions.updateGaltConfirmationStatusSuccess({
                evaluation: res,
                loadingCells
              }));
            }),
            catchError((error) => of(IcsEvaluationActions.updateGaltConfirmationStatusFailure({ error, id: evaluationId, loadingCells })))
          )
        )
      );
    },
    { functional: true }
  );

  exportAuditExecution$ = createEffect(
    (rs: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.exportAuditExecution),
        mergeMap(({ icsCampaignId }) =>
          rs.exportAuditExecution(icsCampaignId).pipe(
            map(({ blob, filename }) => {
              this.downloadFileFromBlob(blob, filename || 'audit_engagements_raw_data.csv')
              return IcsEvaluationActions.exportAuditExecutionSuccess({
                filename: filename || 'audit_engagements_raw_data.csv'
              });
            }),
            catchError((error) => of(IcsEvaluationActions.exportAuditExecutionFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  exportIssueFollowUp$ = createEffect(
    (rs: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.exportIssueFollowUp),
        mergeMap(({ icsCampaignId }) =>
          rs.exportIssueFollowUp(icsCampaignId).pipe(
            map(({ blob, filename }) => {
              this.downloadFileFromBlob(blob, filename || 'issue_follow_up_raw_data.csv')
              return IcsEvaluationActions.exportIssueFollowUpSuccess({
                filename: filename || 'issue_follow_up_raw_data.csv'
              });
            }),
            catchError((error) => of(IcsEvaluationActions.exportIssueFollowUpFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  exportInspection$ = createEffect(
    (rs: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.exportInspection),
        mergeMap(({ icsCampaignId }) =>
          rs.exportInspection(icsCampaignId).pipe(
            map(({ blob, filename }) => {
              this.downloadFileFromBlob(blob, filename || 'inspections_raw_data.csv')
              return IcsEvaluationActions.exportInspectionSuccess({
                filename: filename || 'inspections_raw_data.csv'
              });
            }),
            catchError((error) => of(IcsEvaluationActions.exportInspectionFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  exportSanction$ = createEffect(
    (rs: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.exportSanction),
        mergeMap(({ icsCampaignId }) =>
          rs.exportSanction(icsCampaignId).pipe(
            map(({ blob, filename }) => {
              this.downloadFileFromBlob(blob, filename || 'sanctions_raw_data.csv')
              return IcsEvaluationActions.exportSanctionSuccess({
                filename: filename || 'sanctions_raw_data.csv'
              });
            }),
            catchError((error) => of(IcsEvaluationActions.exportSanctionFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  exportComplaints$ = createEffect(
    (rs: IcsEvaluationService = inject(IcsEvaluationService)) => {
      return this.actions$.pipe(

        ofType(IcsEvaluationActions.exportCompliants),
        mergeMap(({ icsCampaignId }) =>
          rs.exportComplaints(icsCampaignId).pipe(
            map(({ blob, filename }) => {
              this.downloadFileFromBlob(blob, filename || 'complaints_raw_data.csv')
              return IcsEvaluationActions.exportCompliantsSuccess({
                filename: filename || 'complaints_raw_data.csv'
              });
            }),
            catchError((error) => of(IcsEvaluationActions.exportCompliantsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  downloadFileFromBlob(blob: Blob, filename: string) {
    const link = window.document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type);
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }



  constructor(private actions$: Actions) { }
}
